import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiClient';
import shortIllu from '../assets/dwarf.svg';
import footIllu from '../assets/foot.svg';
import aQRCode from '../assets/a_qrcode.svg';
import shortUrlIllu from '../assets/shortUrlIllu.svg';
import dashboardIllu from '../assets/dashboardIllu.svg';

const Home = () => {
  const [url, setUrl] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [expirationMessage, setExpirationMessage] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    document.title = 'ä.dk - The short short URLs!';

    // Create and update meta tags
    const metaTags = {
      'description': 'Create shorter, cleaner QR codes with our URL shortener. Free 7-day URLs with instant QR code generation.',
      'og:title': 'ä.dk - URL Shortener',
      'og:description': 'Create shorter, cleaner QR codes with our URL shortener',
      'og:type': 'website',
      'og:url': 'https://ä.dk',
      'twitter:card': 'summary',
      'twitter:title': 'ä.dk - URL Shortener',
      'twitter:description': 'Create shorter, cleaner QR codes with our URL shortener'
    };

    // Add or update meta tags
    Object.entries(metaTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);

      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    // Cleanup function to remove meta tags when component unmounts
    return () => {
      Object.keys(metaTags).forEach(name => {
        const meta = document.querySelector(`meta[name="${name}"]`) ||
          document.querySelector(`meta[property="${name}"]`);
        if (meta) {
          document.head.removeChild(meta);
        }
      });
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const data = await apiClient.shortenFreeUrl(url);
      setShortUrl(`/${data.shortId}`);
      setQrCode(data.qrCode);
      setSuccess('URL shortened successfully!');
      document.body.classList.add('shortened');

      // Show expiration message for free users
      if (!user?.subscription?.status) {
        setExpirationMessage('This URL will expire in 7 days');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error.message || 'Failed to shorten URL');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (qrCode, shortId) => {
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `qr-code-${shortId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="home-container">
      <div>
        <Navigation />
        <main className="main-content">
          <section className="main">
            <div className="container">
              <div className="flex splash">
                <div className="col">
                  <h1 className="heading-1">The <span className="p-relative"><span className="span1">short</span><span className="span2" aria-hidden="true">short</span></span> short URLs!</h1>
                  <p className="bigger">With ä.dk, you can easily shorten your URLs and generate QR codes.</p>
                  <p>Shorter URLs means cleaner QR codes and faster scans!</p>
                  <form onSubmit={handleSubmit} className="form-container">
                    <label htmlFor="url" style={{ textAlign: 'center', marginBottom: '1em', display: 'block' }}>Enter and shorten your URL for a 7-day short URL and QR-code!</label>
                    <div className="form-group">
                      <input
                        id="url"
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://"
                        pattern="https?://.+"
                        required
                        className="form-input"
                      />
                      <button type="submit" className="button button-primary" disabled={!!shortUrl}>
                        {!!shortUrl ? 'URL Shortened!' : 'Shorten URL'}
                      </button>
                    </div>
                  </form>
                  {error && <div className="error-message">{error}</div>}
                </div>
                <div className="col">
                  <img src={shortIllu} className="dwarf illu img-responsive" />
                </div>
              </div>
              {shortUrl && (
                <div className="result-container">
                  {success && <div className="success-message">{success}</div>}
                  <div className="url-info">
                    <div className="url-message">
                      <p>
                        <strong>Short URL:</strong>{" "}
                        <a href={`https://ä.dk${shortUrl}`} target="_blank" rel="noreferrer">
                          https://ä.dk{shortUrl}
                        </a>
                      </p>
                      <div className="qr-code-container">
                        <button
                          onClick={() => handleDownload(qrCode, shortUrl.split('/').pop())}
                          className="button button-secondary"
                        >
                          <img src={qrCode} alt="QR Code" className="qr-code" />
                          Download QR Code
                        </button>
                      </div>
                      {expirationMessage && (
                        <div className="expiration-message">
                          {expirationMessage}
                        </div>
                      )}
                    </div>
                    <div className="upgrade">Want to keep and manage 50 shorturls like this? <a href="/pricing">Upgrade now!</a></div>
                  </div>
                </div>
              )}
            </div>
          </section>
          <section className="sub-1">
            <div className="container">
              <div className="flex stretch">
                <div className="col">
                  <div className="card point">
                    <h4>Short URLs</h4>
                    <a href="https://ä.dk/short" target="_blank" rel="noreferrer">
                      <img src={shortUrlIllu} style={{ width: '150px' }} className="illu" alt="ä.dk QR Code example" />
                    </a>
                    <p>Create ultra-short links in seconds. Free for 7 days or subscribe for more.</p>
                  </div></div>
                <div className="col">
                  <div className="card point">
                    <h4>Tiny QR Codes</h4>
                    <a href="https://ä.dk/short" target="_blank" rel="noreferrer">
                      <img src={aQRCode} style={{ width: '100px', borderRadius: '2px', backgroundColor: '#fff', padding: '.25em' }} className="illu" alt="ä.dk QR Code example" />
                    </a>
                    <p>Shorter URLs mean bulkier, faster-scanning QR codes.</p>
                  </div></div>
                <div className="col">
                  <div className="card point">
                    <h4>URL Management</h4>
                    <a href="https://app.ä.dk/pricing" target="_blank" rel="noreferrer">
                      <img src={dashboardIllu} style={{ width: '150px' }} className="illu" alt="ä.dk QR Code example" />
                    </a>
                    <p>Track, organize, and keep up to 50 links with a subscription.</p>
                  </div></div>
              </div>
            </div>
          </section>
          <section className="sub-2">
            <div className="container">
              <div className="flex">
                <div className="col">
                  <img src={footIllu} className="foot illu img-responsive" />
                </div>
                <div className="col">
                  <h2>Why use an URL shortener?</h2>
                  <p><b>Ä.dk URL shortener is a powerful tool that simplifies long, complex links into compact, easy-to-share URLs.</b></p>
                  <p>Whether for personal use, marketing campaigns, or business needs, here are some key advantages:</p>
                  <ul>
                    <li>
                      <b>Improved Readability & Shareability</b><br />
                      Shortened URLs are cleaner and easier to share in emails, social media posts, printed materials, or even spoken aloud. No more long, cluttered links!</li>
                    <li><b>Better QR Codes</b><br />
                      Shorter URLs mean QR codes with less data, making them faster to scan and visually cleaner, enhancing user experience.</li>
                    <li><b>Enhanced Tracking & Analytics</b><br />
                      Most URL shorteners provide insights into click rates, locations, and device types, helping you understand your audience and optimize engagement.</li>
                    <li><b>Custom Branding & Trust</b><br />
                      With a subscription to ä.dk, you can easily update destination URLs without modifying the short link, perfect for marketing campaigns and promotions.</li>
                  </ul>
                  <p>By using ä.dk URL shortener, you make your links more user-friendly, trackable, and efficient, whether for business, marketing, or everyday convenience.</p>
                </div>
              </div>
            </div>
          </section>

        </main>
        <Footer />
      </div>

    </div>
  );
};

export default Home;